import { Channel, InstallableApp } from 'client/model/skill';
import { ApkInfoWithTargets, InstallableAppFormSettings } from 'client/types';
import { getCanEditAsAdmin } from 'client/utils';
import { v4 as uuidv4 } from 'uuid';

import {
    InstallableAppPublishingSettings,
    InstallableAppSettings,
    LogoDto,
    LogoImage,
    SkillAccess,
    TLogoImage,
} from '../../model/settings';

import { getParameterWithDefault } from './common';

const initialValues: InstallableAppFormSettings = {
    activationPhrases: [],
    brandIsVerified: false,
    brandVerificationWebsite: '',
    logoId: '',
    logoUrl: '',
    secondaryLogoId: '',
    secondaryLogoUrl: '',
    developerName: '',
    description: '',
    noteForModerator: '',
    email: '',
    emailIsVerified: false,
    isTrusted: false,
    hasMonetization: false,
    category: '',
    hideInStore: false,
    packageName: '',
    name: '',
    skillAccess: SkillAccess.Private,
    secondaryTitle: '',
    ageRestriction: undefined,
    apkInfosWithTargets: [],
    screenshots: [],
    eulaAgreement: undefined,
    privacyAgreement: undefined,
    voiceControlEnable: false,
    isAdvancedVoiceControlSupported: false,
};

const getLogo = (settings: { logos?: LogoDto[] }, logoType: TLogoImage) => {
    if (!settings || !('logos' in settings) || !settings.logos || !Array.isArray(settings.logos))
        return { imageId: undefined, url: undefined };

    const logoIndex = settings.logos.findIndex((logo) => logo.imageType === logoType);

    if (logoIndex === -1) return { imageId: undefined, url: undefined };

    const logoDto = settings.logos[logoIndex];

    return { imageId: logoDto?.imageId, url: logoDto?.url };
};

export const getSquareLogoUrl = (aliceApp: InstallableApp) => {
    if (aliceApp.onAir) {
        const { url: squareLogoUrl } = getLogo(aliceApp, LogoImage.square);
        const logoUrl = aliceApp.logoUrl;

        return squareLogoUrl || logoUrl || '';
    } else {
        const { url: squareLogoDraftUrl } = getLogo(aliceApp.draft, LogoImage.square);
        const draftLogoUrl = aliceApp.draft?.logoUrl;

        return squareLogoDraftUrl || draftLogoUrl || '';
    }
};

export const getSquareLogoForm = (aliceApp: InstallableApp, isDraft: boolean) => {
    if (isDraft) {
        const { url: squareLogoDraftUrl } = getLogo(aliceApp.draft, LogoImage.square);
        const draftLogoUrl = aliceApp.draft?.logoUrl;

        return squareLogoDraftUrl || draftLogoUrl || '';
    } else {
        const { url: squareLogoUrl } = getLogo(aliceApp, LogoImage.square);
        const logoUrl = aliceApp.logoUrl;

        return squareLogoUrl || logoUrl || '';
    }
};

const mapApkInfosWithTargetsSettingsToValues = (apkInfos: ApkInfoWithTargets[]): ApkInfoWithTargets[] => {
    if (!apkInfos?.length)
        return [
            {
                appVersionId: undefined,
                url: undefined,
                versionNotes: undefined,
                versionCode: undefined,
                versionName: undefined,
                _creationId: uuidv4(),
            },
        ];

    return apkInfos.map((apkInfo) => {
        const id = apkInfo.appVersionId || uuidv4();
        apkInfo._creationId = id;
        return apkInfo;
    });
};

export function mapValuesToInstallableAppSettings(
    values: InstallableAppFormSettings,
    channel: Channel,
): Partial<InstallableAppSettings> {
    const additionalLogos = [];
    if (values.squareLogoId) {
        additionalLogos.push({ imageType: LogoImage.square, imageId: values.squareLogoId });
    }
    if (values.backgroundImageId) {
        additionalLogos.push({ imageType: LogoImage.backgroundImage, imageId: values.backgroundImageId });
    }

    return {
        activationPhrases: values.activationPhrases,
        channel,
        logoId: values.logoId,
        secondaryLogoId: values.secondaryLogoId,
        additionalLogos,
        packageName: values.packageName,
        noteForModerator: values.noteForModerator,
        name: values.name,
        ageRestriction: values.ageRestriction,
        apkInfosWithTargets: values.apkInfosWithTargets?.filter((apkInfo) => !!apkInfo?.appVersionId),
        screenshots: values.screenshots,
        privacyAgreement: values.privacyAgreement,
        eulaAgreement: values.eulaAgreement,
        skillAccess: values.skillAccess,
        isTrusted: getCanEditAsAdmin() ? values.isTrusted : undefined,

        backendSettings: {
            isVoiceControlDisabled: !values.voiceControlEnable,
            isAdvancedVoiceControlSupported: values.isAdvancedVoiceControlSupported,
        },

        publishingSettings: {
            email: values.email,
            brandVerificationWebsite: values.brandVerificationWebsite,
            description: values.description,
            developerName: values.developerName,
            category: values.category,
            secondaryTitle: values.secondaryTitle,
            hasMonetization: values.hasMonetization,
        },
    };
}

export function mapInstallableAppSettingsToValues(settings: InstallableAppSettings): InstallableAppFormSettings {
    const {
        description,
        category,
        developerName,
        secondaryTitle,
        email,
        brandIsVerified,
        brandVerificationWebsite,
        emailIsVerified,
        hasMonetization,
    } = {
        ...initialValues,
        ...settings.publishingSettings,
    } as Required<InstallableAppPublishingSettings>;

    const activationPhrases =
        settings.activationPhrases && settings.activationPhrases.filter(Boolean).length > 0
            ? settings.activationPhrases
            : [settings.name];

    const { imageId: squareLogoId, url: squareLogoUrl } = getLogo(settings, LogoImage.square);
    const { imageId: backgroundImageId, url: backgroundImageUrl } = getLogo(settings, LogoImage.backgroundImage);

    return {
        ...initialValues,

        activationPhrases,
        backgroundImageId,
        backgroundImageUrl,
        brandIsVerified,
        brandVerificationWebsite: brandVerificationWebsite || initialValues.brandVerificationWebsite,
        description,
        developerName,
        secondaryTitle,
        category,
        isTrusted: !!settings.isTrusted,
        email,
        emailIsVerified,
        hasMonetization,
        logoId: getParameterWithDefault(settings, initialValues, 'logoId'),
        logoUrl: getParameterWithDefault(settings, initialValues, 'logoUrl'),
        squareLogoId,
        squareLogoUrl,
        secondaryLogoId: settings?.secondaryLogo?.imageId || '',
        secondaryLogoUrl: settings?.secondaryLogo?.imageUrl || '',
        packageName: getParameterWithDefault(settings, initialValues, 'packageName'),
        noteForModerator: getParameterWithDefault(settings, initialValues, 'noteForModerator'),
        name: getParameterWithDefault(settings, initialValues, 'name'),
        ageRestriction: getParameterWithDefault(settings, initialValues, 'ageRestriction'),
        apkInfosWithTargets: mapApkInfosWithTargetsSettingsToValues(settings.apkInfosWithTargets),
        screenshots: getParameterWithDefault(settings, initialValues, 'screenshots'),
        privacyAgreement: getParameterWithDefault(settings, initialValues, 'privacyAgreement'),
        eulaAgreement: getParameterWithDefault(settings, initialValues, 'eulaAgreement'),
        skillAccess: getParameterWithDefault(settings, initialValues, 'skillAccess'),
        voiceControlEnable:
            settings?.backendSettings?.isVoiceControlDisabled === undefined
                ? false
                : !settings?.backendSettings?.isVoiceControlDisabled,
        isAdvancedVoiceControlSupported: !!settings?.backendSettings?.isAdvancedVoiceControlSupported,
    };
}
